/**********************************
 * Settings
 **********************************/
import { useGlobalUIStore } from "@/stores/useGlobalUIStore.js";

/**********************************
 * Composables Definition
 **********************************/
/**
 * 전역 로딩 상태 관리
 *
 * @function useGlobalLoadingState
 * @property {Ref<boolean>} isLoading 페이지 로딩 상태
 * @property {Object} uiStore UI 스토어 객체
 */
export function useGlobalLoadingState() {
  const uiStore = useGlobalUIStore();
  const { isLoading } = storeToRefs(uiStore);

  return {
    isLoading,
    uiStore,
  };
}

/**
 * 디바이스 정보 설정
 *
 * @function useDeviceInfo
 * @property {Function} setupDeviceInfo 디바이스 정보를 설정하는 함수
 */
export function useDeviceInfo() {
  // Device 체크 설정
  const setupDeviceInfo = () => {
    const app = document.querySelector("#app");
    if (app && window.getDevice) {
      app.dataset.targetDevice = window.getDevice().type;
      app.dataset.deviceDetail = window.getDevice().detail;
    }
  };

  return {
    setupDeviceInfo,
  };
}

/**
 * HTML 태그에 클래스 추가/제거 관리
 *
 * @function useHtmlAddClass
 * @property {Function} setupHtmlClass HTML 태그에 라우트 메타 정보의 클래스를 적용하는 함수
 */
export function useHtmlAddClass() {
  const route = useRoute();
  const router = useRouter();

  // HTML 태그에 클래스 적용
  const applyHtmlClass = (_classes) => {
    if (!_classes) return;

    const htmlClasses = Array.isArray(_classes) ? _classes : [_classes];
    htmlClasses.forEach((_class) => {
      document.documentElement.classList.add(_class);
    });
  };

  // HTML 태그에서 클래스 제거
  const removeHtmlClass = (_classes) => {
    if (!_classes) return;

    const htmlClasses = Array.isArray(_classes) ? _classes : [_classes];
    htmlClasses.forEach((_class) => {
      document.documentElement.classList.remove(_class);
    });
  };

  // 라우트 변경 시 HTML 클래스 업데이트
  const setupHtmlClass = () => {
    // 초기 라우트에 클래스 적용
    if (route.meta.htmlClass) {
      applyHtmlClass(route.meta.htmlClass);
    }

    // 라우트 변경 감지
    router.beforeEach((_to, _from, _next) => {
      // 이전 라우트의 클래스 제거
      if (_from.meta.htmlClass) {
        removeHtmlClass(_from.meta.htmlClass);
      }

      // 새 라우트의 클래스 추가
      if (_to.meta.htmlClass) {
        applyHtmlClass(_to.meta.htmlClass);
      }

      _next();
    });
  };

  return {
    setupHtmlClass,
  };
}
